import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores";
import { Skeleton } from "@/components/ui";
import { PermissionDenied } from "./PermissionDenied";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredAnyPermission?: string[];
}

export function ProtectedRoute({
  children,
  requiredPermission,
  requiredAnyPermission,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="space-y-4">
          <Skeleton className="h-12 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userPermissions = user?.role?.permissions?.map((p) => p.slug) ?? [];

  if (requiredPermission && !userPermissions.includes(requiredPermission)) {
    return <PermissionDenied requiredPermission={requiredPermission} />;
  }

  if (
    requiredAnyPermission &&
    requiredAnyPermission.length > 0 &&
    !requiredAnyPermission.some((p) => userPermissions.includes(p))
  ) {
    return (
      <PermissionDenied
        requiredPermission={requiredAnyPermission.join(" or ")}
      />
    );
  }

  return <>{children}</>;
}
